/**
 * The build panel: the palette that makes a block, and the stack it goes into
 * (DESIGN §9.3).
 *
 * The palette is *permanent*. It used to be a toggle that opened a menu over
 * the list, which meant the answer to "how do I put something on this screen?"
 * was hidden behind a button labelled with the question. A grid of tiles costs
 * less room than the menu did and is the first thing in the panel.
 *
 * The core six come first, then one tile per block every *enabled* module
 * contributes (MODULES §7.4) — the same rule the action picker follows, and
 * the same gestures: click to add, drag to place. Nothing here knows what any
 * of those blocks are; the label, the tooltip and the fields all come out of
 * the manifest (M6).
 *
 * A tile can be clicked or dragged: a click adds the block after whatever is
 * selected (so a run of them builds a screen in order), a drag drops it into a
 * slot — on this list or on the phone, both read the same payload (`dnd.ts`).
 *
 * A block can be dragged to a new place, and the arrow keys still reorder the
 * selected row, which is the whole feature on its own for anyone not using a
 * mouse (D12): native drag events, no library.
 *
 * *Image* never makes an empty block. The page it belongs to answers the tile
 * by opening the file dialog, and the picture arrives with the block.
 */

import { useRef, useState } from "react";

import type { ModuleOut } from "../api/types";
import { useI18n, type Translate } from "../i18n";
import {
  BLOCK_MIME,
  BLOCK_TYPE_MIME,
  carriesBlock,
  carriesBlockType,
  draggedBlock,
  draggedPaletteItem,
  filesOf,
  hasFiles,
  paletteValue,
  slotAt,
  type PaletteItem,
} from "./dnd";
import { BlockIcon, IconDuplicate, IconGrip, IconScreen, IconTrash } from "./icons";
import { moduleBlockChoices, moduleBlockName, moduleBlockOf } from "./modules";
import {
  BLOCK_TYPES,
  DEFAULTS,
  MODULE_BLOCK,
  isModuleRow,
  type Block,
  type BlockType,
} from "./spec";

/** The insertion line: above this row, or below it when it is the last one. */
function dropSlot(
  slot: number | null,
  index: number,
  count: number,
): "before" | "after" | undefined {
  if (slot === index) return "before";
  return slot === count && index === count - 1 ? "after" : undefined;
}

function summary(block: Block, t: Translate, modules: ModuleOut[]): string {
  switch (block.type) {
    case "text":
      return block.text.slice(0, 40);
    case "button":
      return block.label;
    case "image":
      return block.alt || t("design.block.image");
    case "spacer":
      return block.flex ? t("design.spacer.flex") : `${block.height ?? 16} dp`;
    case "divider":
      return `${block.thickness ?? 1} dp`;
    case "status":
      return (
        (block.rows ?? DEFAULTS.status.rows)
          // A module row has no key of ours to look up: it is named by the
          // release, so the list shows the reference itself (MODULES §7.2).
          .map((row) => (isModuleRow(row) ? row : t(`design.row.${row}`)))
          .join(", ")
      );
    case MODULE_BLOCK: {
      // The reference under the label, and the same word a switched-off
      // module's action gets in the picker — a row that will not be on the
      // phone has to say so where the customer is looking (MODULES §8.6).
      const module = modules.find((entry) => entry.id === block.module);
      const off = !module?.enabled || !moduleBlockOf(modules, block);
      // …but only once the catalogue is here: while `GET /modules` is in
      // flight every block would read as unavailable, which is a lie.
      const marker = modules.length > 0 && off ? t("design.actionGroup.off") : "";
      return [`${block.module}.${block.block}`, marker].filter(Boolean).join(" · ");
    }
  }
}

/** What a row is called: the block type, or a module block's own label. */
function kindLabel(block: Block, t: Translate, modules: ModuleOut[], lang: string): string {
  return block.type === MODULE_BLOCK
    ? moduleBlockName(modules, block, lang)
    : t(`design.block.${block.type}`);
}

export default function BlockList({
  blocks,
  selectedId,
  onSelect,
  onSelectScreen,
  onAdd,
  onMove,
  onReorder,
  onImportFiles,
  onDuplicate,
  onRemove,
  modules = [],
}: {
  blocks: Block[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onSelectScreen: () => void;
  /** A palette tile. `at` is the slot a dragged tile was dropped in; without
   *  one the page decides where a new block goes. */
  onAdd: (item: PaletteItem, at?: number) => void;
  onMove: (id: string, delta: number) => void;
  /** A block dragged to a slot in the stack, 0..blocks.length. */
  onReorder: (id: string, to: number) => void;
  /** Files dropped on the list; each one becomes a block at the end. */
  onImportFiles: (files: File[]) => void;
  onDuplicate: (id: string) => void;
  onRemove: (id: string) => void;
  /** The catalogue the release carries; `[]` until `GET /modules` answers. */
  modules?: ModuleOut[];
}) {
  const { t, lang } = useI18n();
  const [slot, setSlot] = useState<number | null>(null);
  const list = useRef<HTMLUListElement>(null);
  // After the core six, because they are the ones every design is made of —
  // and one tile per block of every *enabled* module, exactly as the action
  // picker offers only enabled modules' actions (MODULES §8.3).
  const contributed = moduleBlockChoices(modules, lang);

  const rows = () =>
    Array.from(list.current?.querySelectorAll<HTMLElement>("li[data-block-id]") ?? []);

  return (
    <div className="design-build">
      <section className="design-section">
        <h3 className="design-section-label">{t("design.addBlock")}</h3>
        <div className="design-palette">
          {BLOCK_TYPES.map((type) => (
            <PaletteTile
              key={type}
              item={{ kind: "block", type }}
              icon={type}
              label={t(`design.block.${type}`)}
              /* The description is a tooltip rather than a line of prose under
                 every tile: six sentences would make the palette taller than
                 the stack it is there to fill. */
              title={t(`design.blockHint.${type}`)}
              onAdd={onAdd}
            />
          ))}
          {contributed.map((choice) => (
            <PaletteTile
              key={`${choice.module}:${choice.block}`}
              item={{ kind: "module", module: choice.module, block: choice.block }}
              icon="module"
              /* The manifest's own words, in the customer's language: nothing
                 here knows what a web page is (M6). */
              label={choice.label}
              title={choice.description}
              onAdd={onAdd}
            />
          ))}
        </div>
        <p className="muted small design-hint">{t("design.paletteHint")}</p>
      </section>

      <section className="design-section">
        <h3 className="design-section-label">{t("design.blocks")}</h3>

        {/* The screen is not a block, so it is not in the stack: it is pinned
            above it, the way a document's page setup sits above its content. */}
        <button
          type="button"
          className={selectedId === null ? "design-screen-row on" : "design-screen-row"}
          aria-current={selectedId === null}
          onClick={onSelectScreen}
        >
          <IconScreen size={16} />
          <span className="design-block-text">
            <span className="design-block-kind">{t("design.theme")}</span>
            <span className="design-block-summary muted small">{t("design.screenHint")}</span>
          </span>
        </button>

        <ul
          ref={list}
          className="design-block-list"
          onDragOver={(event) => {
            const files = hasFiles(event.dataTransfer);
            const made = carriesBlockType(event.dataTransfer);
            if (!files && !made && !carriesBlock(event.dataTransfer)) return;
            // Without preventDefault the browser refuses the drop outright.
            event.preventDefault();
            event.dataTransfer.dropEffect = files || made ? "copy" : "move";
            // A file dropped here joins the end of the stack; a block being
            // moved and a tile being placed both land against the row the
            // pointer is on.
            setSlot(files ? blocks.length : slotAt(rows(), event.clientY));
          }}
          onDragLeave={(event) => {
            const to = event.relatedTarget;
            if (to instanceof Node && event.currentTarget.contains(to)) return;
            setSlot(null);
          }}
          onDrop={(event) => {
            event.preventDefault();
            setSlot(null);
            const moved = draggedBlock(event.dataTransfer);
            if (moved) {
              // Read from the pointer rather than from the state the last
              // `dragover` left: the two agree, and only one of them can be
              // stale by the time the drop lands.
              onReorder(moved, slotAt(rows(), event.clientY));
              return;
            }
            const made = draggedPaletteItem(event.dataTransfer);
            if (made) {
              onAdd(made, slotAt(rows(), event.clientY));
              return;
            }
            const dropped = filesOf(event.dataTransfer);
            if (dropped.length > 0) onImportFiles(dropped);
          }}
        >
          {blocks.map((block, index) => (
            <li
              key={block.id}
              data-block-id={block.id}
              data-drop-slot={dropSlot(slot, index, blocks.length)}
              className={block.id === selectedId ? "on" : undefined}
              aria-current={block.id === selectedId}
              draggable
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = "move";
                event.dataTransfer.setData(BLOCK_MIME, block.id);
              }}
              onDragEnd={() => setSlot(null)}
            >
              <span className="design-block-handle" title={t("design.dragHint")}>
                <IconGrip size={14} />
              </span>
              <button
                type="button"
                className="design-block-pick"
                onClick={() => onSelect(block.id)}
                onKeyDown={(event) => {
                  // Arrow keys move the block, the way a list of steps should:
                  // the ↑↓ buttons are gone from the row, this is not.
                  if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                    event.preventDefault();
                    onMove(block.id, event.key === "ArrowUp" ? -1 : 1);
                  } else if (event.key === "Delete" || event.key === "Backspace") {
                    event.preventDefault();
                    onRemove(block.id);
                  } else if (event.key.toLowerCase() === "d" && (event.metaKey || event.ctrlKey)) {
                    event.preventDefault();
                    onDuplicate(block.id);
                  }
                }}
              >
                <BlockIcon type={block.type} size={15} />
                <span className="design-block-text">
                  <span className="design-block-kind">
                    {kindLabel(block, t, modules, lang)}
                  </span>
                  <span className="design-block-summary muted small">
                    {summary(block, t, modules)}
                  </span>
                </span>
              </button>
              {/* Kept out of the way until the row is wanted: four controls on
                  every row of a stack is a wall of glyphs, and the two that
                  are left here are the two that are not a keystroke away. */}
              <span className="design-block-actions">
                <button
                  type="button"
                  className="icon-btn"
                  title={t("design.duplicate")}
                  aria-label={t("design.duplicate")}
                  onClick={() => onDuplicate(block.id)}
                >
                  <IconDuplicate size={15} />
                </button>
                <button
                  type="button"
                  className="icon-btn danger"
                  title={t("design.remove")}
                  aria-label={t("design.remove")}
                  onClick={() => onRemove(block.id)}
                >
                  <IconTrash size={15} />
                </button>
              </span>
            </li>
          ))}
        </ul>

        {blocks.length === 0 && <p className="muted small design-hint">{t("design.noBlocks")}</p>}

        {/* The one sentence that answers "how do I move this thing?" — a design
            is a reflowing stack, so placing a block is order, `align`,
            `fullWidth` and spacers, never x and y (`dnd.ts`). */}
        <p className="muted small design-stack-hint">{t("design.stackHint")}</p>
      </section>
    </div>
  );
}

/**
 * One tile. A core type and a module's block are the same gesture — click to
 * add, drag to place — so they are the same component, and the only thing
 * that tells them apart is the payload the drag carries (`dnd.ts`).
 */
function PaletteTile({
  item,
  icon,
  label,
  title,
  onAdd,
}: {
  item: PaletteItem;
  icon: BlockType | typeof MODULE_BLOCK;
  label: string;
  title: string;
  onAdd: (item: PaletteItem, at?: number) => void;
}) {
  return (
    <button
      type="button"
      className="design-tile"
      title={title}
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "copy";
        event.dataTransfer.setData(BLOCK_TYPE_MIME, paletteValue(item));
      }}
      onClick={() => onAdd(item)}
    >
      <BlockIcon type={icon} size={19} />
      <span>{label}</span>
    </button>
  );
}
